import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronUp,
  Eye,
  QrCode,
  ScanLine,
  TicketPercent,
  Trash2,
  Wallet,
} from "lucide-react";
import {
  COUPON_CATEGORY_I18N_KEYS,
  formatCouponDate,
  getCouponTerms,
  isQrCoupon,
  type BusinessCoupon,
} from "@/utils/businessCoupon";

/** Lifetime reach & engagement for one coupon, from the dashboard stats API. */
export type BusinessCouponStats = {
  reads?: number;
  readers?: number;
  redeemed?: number;
  used?: number;
};

interface BusinessCouponCardProps {
  coupon: BusinessCoupon;
  stats?: BusinessCouponStats;
  onShowQr?: (coupon: BusinessCoupon) => void;
  onScan?: () => void;
  onDelete?: (coupon: BusinessCoupon) => void;
}

/**
 * Coupon card for the business coupon centre — same visual language as the RN
 * `AnimatedCouponCard`, but the trailing action is the QR redemption code
 * instead of `Redeem`.
 */
const BusinessCouponCard: React.FC<BusinessCouponCardProps> = ({
  coupon,
  stats,
  onShowQr,
  onScan,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();
  const [showTerms, setShowTerms] = useState(false);

  const isZh = String(i18n?.language ?? "").toLowerCase().startsWith("zh");
  const name = String(coupon?.coupon_name ?? "");
  const image = String(coupon?.coupon_image ?? "");
  const categoryCode = String(coupon?.coupon_category ?? "").trim();
  const category = categoryCode
    ? t(COUPON_CATEGORY_I18N_KEYS[categoryCode] ?? categoryCode)
    : "";
  const terms = getCouponTerms(coupon, isZh);
  const originalPrice = coupon?.original_price;
  const issuedQuantity =
    coupon?.issued_quantity === undefined || coupon?.issued_quantity === null
      ? "-"
      : String(coupon.issued_quantity).trim() || "-";
  const expiry = formatCouponDate(coupon?.expiry_date, i18n?.language);
  const qrEnabled = isQrCoupon(coupon);

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md">
      <div className="flex items-center gap-3 p-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50">
          {image ? (
            <img
              src={image}
              alt={name || t("Coupon")}
              loading="lazy"
              className="h-full w-full object-contain"
            />
          ) : (
            <TicketPercent size={24} className="text-gray-300" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-bold leading-5 text-gray-900">
            {name || t("Coupon")}
          </h3>
          {category && (
            <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">{category}</p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {originalPrice ? (
              <span className="font-bold text-[#ffa217]">
                {t("Original Price")}: {String(originalPrice)} {t("HKD")}
              </span>
            ) : null}
            {expiry ? (
              <span className="font-semibold text-gray-700">
                {t("Expiry Date")}: {expiry}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {terms && (
        <div className="px-4">
          <p
            className={`text-xs leading-4 text-gray-400 ${
              showTerms ? "" : "line-clamp-2"
            }`}
          >
            {terms}
          </p>
          <button
            type="button"
            onClick={() => setShowTerms((value) => !value)}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#ffa217]"
          >
            {showTerms ? (
              <>
                {t("Terms & Conditions")}
                <ChevronUp size={12} />
              </>
            ) : (
              t("Tap to view terms")
            )}
          </button>
        </div>
      )}

      {stats ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Eye size={12} className="text-gray-400" />
            {t("Reads")}: {toCount(stats.reads)}
          </span>
          <span className="inline-flex items-center gap-1">
            <TicketPercent size={12} className="text-gray-400" />
            {t("redeemed")}: {toCount(stats.redeemed)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Wallet size={12} className="text-gray-400" />
            {t("Used")}: {toCount(stats.used)}
          </span>
        </div>
      ) : null}

      <div className="mt-auto flex items-center justify-between gap-2 px-4 py-3">
        <span className="truncate text-[11px] text-gray-400">
          {t("Issued Quantity")}: {issuedQuantity}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {qrEnabled && (
            <button
              type="button"
              onClick={() => onShowQr?.(coupon)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#f59200]"
            >
              <QrCode size={13} />
              {t("Show QR")}
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(coupon)}
              aria-label={t("Delete")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition hover:border-red-400 hover:text-red-500"
            >
              <Trash2 size={13} />
              {t("Delete")}
            </button>
          )}
          <button
            type="button"
            onClick={onScan}
            aria-label={t("Business: scan customer coupon")}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
          >
            <ScanLine size={13} />
            {t("Scan")}
          </button>
        </div>
      </div>
    </article>
  );
};

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default BusinessCouponCard;
